import { ArrowLeft } from '@velocity-exchange/icons';
import * as RadixDialog from '@radix-ui/react-dialog';
import {
	CSSProperties,
	RefCallback,
	RefObject,
	useCallback,
	useRef,
} from 'react';
import XButton from 'src/components/Buttons/XButton';
import ScrollShadows from 'src/components/ScrollShadows';
import useModal from 'src/components/Modals/hooks/useModal';
import {
	MODAL_BG_CLASSNAME,
	MODAL_PADDING,
} from 'src/components/primitives/BaseModal/consts';
import { ConditionalAsChild } from 'src/components/primitives/ConditionalAsChild';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import { TabOption } from 'src/components/primitives/Tabs/types';
import { Z_INDEX } from 'src/constants/zIndex';
import useDisableScroll from 'src/hooks/useDisableScroll';
import { cj, cm, hasClass } from 'src/utils/classNames';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';
import { getIsMediaHover } from 'src/utils/browser';

export interface ModalContainerProps extends WithClassName, WithChildren {
	onClose?: () => void;
	/**
	 * Only necessary if you need to explicitly control the open state.
	 */
	open?: boolean;
	/**
	 * Renders the children without the overlay wrapper.
	 * Note, you'll need to manually handle positioning in this case.
	 */
	disableOverlay?: boolean;
	/**
	 * If true, clicking the background overlay will not close the modal.
	 *
	 * Should be used only in cases where an unintentional click may close
	 * a modal that can't be reopened again.
	 */
	disableCloseOnOverlayClick?: boolean;
	initialFocusRef?: RefObject<HTMLElement>;
	containerClassName?: string;
	style?: CSSProperties;
	dataTestId?: string;
}

function ModalContainer({
	open,
	onClose,
	className,
	children,
	disableOverlay,
	disableCloseOnOverlayClick,
	initialFocusRef,
	containerClassName,
	style,
	dataTestId,
}: ModalContainerProps) {
	const containerRef = useRef<HTMLDivElement>(null);

	const onOpenChange = useCallback(
		(newOpenValue: boolean) => {
			if (!newOpenValue) {
				onClose?.();
			}
		},
		[onClose]
	);

	const content = (
		<div
			className={cm(
				'text-text-deafult flex w-full flex-col overflow-hidden border border-stroke-divider',
				// Only apply below classes if custom ones haven't been passed.
				// Using `hasClass` so we don't have to pass in extra `sm:` overrides.
				!hasClass(className, 'w-') &&
					'sm:w-min sm:max-w-[632px] sm:min-w-[416px]',
				!hasClass(className, 'h-') && 'h-full sm:h-auto sm:max-h-[85vh]',
				!hasClass(className, 'rounded') && 'sm:rounded-lg',
				MODAL_BG_CLASSNAME,
				className
			)}
			style={style}
			data-testid={dataTestId}
		>
			{children}
		</div>
	);

	if (disableOverlay) {
		return (
			<ModalContentWrapper
				open={open}
				onOpenChange={onOpenChange}
				initialFocusRef={initialFocusRef}
			>
				{content}
			</ModalContentWrapper>
		);
	}

	return (
		<ModalContentWrapper
			open={open}
			onOpenChange={onOpenChange}
			initialFocusRef={initialFocusRef}
		>
			{/* When the modal is combined with the Radix select, the select overlay can pass */}
			{/* click events to the underlying content. This div is a workaround to prevent that.*/}
			<div
				className={cj(
					'pointer-events-auto fixed inset-0',
					Z_INDEX.modalOverlay
				)}
			/>
			{/* Note, our styled overlay is technically the container, hence the z-index naming. */}
			<div
				className={cm(
					'fixed inset-0 flex items-center justify-center bg-overlay',
					Z_INDEX.modalContainer,
					containerClassName
				)}
				// Important to use pointer down here instead of click, because if there's
				// a slider in the modal and the user quickly drags the thumb so that their
				// pointer goes outside the modal, a click event *may* fire on the overlay.
				onPointerDown={(e) => {
					if (
						// Only close the modal if the target is the container itself (i.e. the overlay).
						containerRef.current === e.target &&
						!disableCloseOnOverlayClick
					) {
						setTimeout(
							() => onOpenChange(false),
							// Close with a delay on non-hover devices to prevent triggering underlying elements.
							!getIsMediaHover() ? 200 : 0
						);
					}
				}}
				onTouchEnd={(e) => {
					// Necessary in combo with `onPointerDown` timeout to prevent triggering
					// underlying elements on non-hover devices.
					if (containerRef.current === e.target) {
						e.preventDefault();
					}
				}}
				ref={containerRef}
			>
				{content}
			</div>
		</ModalContentWrapper>
	);
}

interface ModalContentWrapperProps
	extends WithChildren, Pick<ModalContainerProps, 'initialFocusRef'> {
	open: boolean | undefined;
	onOpenChange: (open: boolean) => void;
}

function ModalContentWrapper({
	open,
	onOpenChange,
	children,
	initialFocusRef,
}: ModalContentWrapperProps) {
	useDisableScroll();

	// Either focus the user's passed in ref, or the content itself.
	const ref = useRef<HTMLDivElement>(null);
	const callbackRef = useCallback<RefCallback<HTMLDivElement>>((node) => {
		if (ref.current) {
			return;
		}

		if (initialFocusRef?.current) {
			initialFocusRef.current.focus();
			return;
		}

		if (node) {
			node.focus();
			ref.current = node;
		}
	}, []);

	return (
		<RadixDialog.Root
			// We render the majority of our modals only when they're supposed to be shown,
			// so usally this can just be `true`. Pass in your own `open` to override.
			open={open ?? true}
			// If `modal` is `true` here, click events on the custom overlay div don't work.
			modal={false}
			onOpenChange={onOpenChange}
		>
			<RadixDialog.Portal>
				<RadixDialog.Content
					// Prevent radix from handling interaction events outside the content. This is to enable scrolling in popups.
					onInteractOutside={(e) => e.preventDefault()}
					// We handle this manually.
					onOpenAutoFocus={(e) => e.preventDefault()}
					// Need to pass this because we're not using `Radix.Description`.
					// See https://www.radix-ui.com/primitives/docs/components/dialog#description.
					aria-describedby={undefined}
					ref={callbackRef}
				>
					{children}
				</RadixDialog.Content>
			</RadixDialog.Portal>
		</RadixDialog.Root>
	);
}

interface BaseModalTitleProps extends WithChildren {
	/**
	 * By default the modal will show a back button if there is a previous modal
	 * in the stack. You can use this to force show it when necessary.
	 */
	showBack?: boolean;
	onBack?: () => void;
	/**
	 * If passed, a close button that invokes this fn will be rendered.
	 */
	onClose?: () => void;
	/**
	 * An element to show at the end of the title, to the left of the close button.
	 */
	endElement?: React.ReactNode;
	/**
	 * An element to show beneath the back button, title, close button row.
	 */
	bottomElement?: React.ReactNode;
	/**
	 * Styles applied to the outermost (root) element that sets root layout and bg styles.
	 */
	className?: string;
	/**
	 * Styles applied to the inner wrapper that sets padding, border, and text styles.
	 */
	innerWrapperClassName?: string;
	/**
	 * Styles applied to the `children` wrapper that sets inner layout styles.
	 */
	childrenWrapperClassName?: string;
	closeButtonTestId?: string;
}

export type ModalTitleProps<T extends string> = BaseModalTitleProps &
	(
		| {
				tabs: TabOption<T>[];
				activeTab: T;
				onTabChange: (tab: T) => void;
				/** Stamps `<prefix>-<tab value>` on each tab. */
				tabTestIdPrefix?: string;
		  }
		| {
				tabs?: never;
				activeTab?: never;
				onTabChange?: never;
				tabTestIdPrefix?: never;
		  }
	);

function ModalTitle<T extends string>({
	showBack,
	onBack,
	onClose,
	className,
	children,
	endElement,
	bottomElement,
	tabs,
	activeTab,
	onTabChange,
	tabTestIdPrefix,
	innerWrapperClassName,
	childrenWrapperClassName,
	closeButtonTestId,
	...rest
}: ModalTitleProps<T>) {
	const scrollableTabsRef = useRef<HTMLDivElement>(null);
	const { goBack, canGoBack } = useModal();

	const handleBack = () => {
		if (canGoBack) {
			goBack();
		}

		onBack?.();
	};

	return (
		<RadixDialog.Title
			className={cm(
				// `flex-col` due to potential tabs
				'flex flex-col justify-between gap-y-sm',
				MODAL_BG_CLASSNAME,
				className
			)}
			{...rest}
		>
			{/* Due to potential tabs, padding and border can't be applied directly to the root element. */}
			{/* So we need this internal wrapper here. Other styles that technically could be applied to */}
			{/* the root (e.g. color) are also applied here simply for convenience (e.g. if overriding). */}
			<div
				className={cm(
					// `flex-col` due to potential bottom element.
					'flex flex-col gap-y-sm',
					'typo-label-md text-text-default',
					'border-b border-stroke-divider',
					[MODAL_PADDING.horizontal, MODAL_PADDING.headerFooterVertical],
					innerWrapperClassName
				)}
			>
				{/* Back button, title, close button row. */}
				<div className="flex items-center justify-between">
					<div
						className={cm(
							'flex items-center gap-x-sm',
							childrenWrapperClassName
						)}
					>
						{(canGoBack || showBack) && (
							<IconButton
								variant="tertiary"
								size="lg"
								icon={ArrowLeft}
								onClick={handleBack}
							/>
						)}
						{children}
					</div>
					{(onClose || endElement) && (
						<div className="flex items-center gap-x-sm">
							{endElement}
							{onClose && (
								<XButton onClick={onClose} dataTestId={closeButtonTestId} />
							)}
						</div>
					)}
				</div>

				{bottomElement}
			</div>

			{tabs && (
				<ScrollShadows
					scrollableRef={scrollableTabsRef}
					gradientFromClassName="from-container-bg"
					className={MODAL_PADDING.horizontal}
				>
					<div
						ref={scrollableTabsRef}
						className="hidden-scroll w-full overflow-x-auto"
					>
						<Tabs
							className="w-full not-sm:gap-x-0"
							options={tabs}
							activeTab={activeTab}
							onTabChange={onTabChange}
							indicatorTrack
							tabTestIdPrefix={tabTestIdPrefix}
						/>
					</div>
				</ScrollShadows>
			)}
		</RadixDialog.Title>
	);
}

export interface ModalBodyProps extends WithClassName, WithChildren {
	/**
	 * This renders `children` directly instead of within a wrapper element.
	 * Note, `children` should be a single React element and it should accept the `className` prop.
	 */
	asChild?: boolean;
	style?: React.CSSProperties;
}

function ModalBody({ asChild, children, className, style }: ModalBodyProps) {
	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="div"
			className={cm(
				'flex flex-1 flex-col gap-y-lg',
				// Prevent dialog content from overflowing the screen
				'hidden-scroll overflow-x-hidden overflow-y-auto',
				'typo-body-xs text-text-secondary',
				[MODAL_PADDING.horizontal, MODAL_PADDING.bodyVertical],
				MODAL_BG_CLASSNAME,
				className
			)}
			style={style}
		>
			{children}
		</ConditionalAsChild>
	);
}

export interface ModalFooterProps extends WithChildren {
	/**
	 * Recipes for customizing:
	 *  - If you want right-aligned buttons, supply `justify-end`.
	 *  - If you want two buttons of equal size, supply `flex-1` to each.
	 *  - If you want your button to take up the full width of the footer, supply `w-full`.
	 *  - By default uses `flex-row`. If you want a column, supply `flex-col` (and probably
	 *    also `item-stretch`). The spacing is already configured (`gap-md`).
	 *  - There is a default `text-text-secondary` applied to text, which is intended
	 *    for info text. If you are rendering error text, supply an appropriate error color.
	 */
	className?: string;
	/**
	 * This renders `children` directly instead of within a wrapper element.
	 * Note, `children` should be a single React element and it should accept the `className` prop.
	 */
	asChild?: boolean;
}

function ModalFooter({ asChild, className, children }: ModalFooterProps) {
	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="div"
			className={cm(
				'flex items-center gap-md text-text-secondary',
				'border-t border-stroke-divider typo-body-xs',
				[MODAL_PADDING.horizontal, MODAL_PADDING.headerFooterVertical],
				MODAL_BG_CLASSNAME,
				className
			)}
		>
			{children}
		</ConditionalAsChild>
	);
}

export default {
	Container: ModalContainer,
	Title: ModalTitle,
	Body: ModalBody,
	Footer: ModalFooter,
};
